import React from "react";

import {
  Button,
  DocumentList,
  ExpandablePanel,
  ExpandablePanelBody,
  ExpandablePanelHeader,
  InfoPanel,
  TextField,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import Child from "../../../models/Person/Child";
import IntegrationResultStatusEnum from "../../../models/Person/IntegrationResultStatusEnum";
import { useChildDocuments } from "../../Request/ChildDataStep/useChildDocuments";
import styles from "./RequestDetailsPage.module.scss";

interface Props {
  emptyValue: string;
  child: Child;
  moveToSpecificStep?: (step: number, substep?: number) => void;
  onExpandablePanelClick?: () => void;
}

export default function ChildDetails({
  emptyValue,
  child,
  moveToSpecificStep,
  onExpandablePanelClick,
}: Props) {
  const { t } = useTranslation();

  const { handleDownloadDocument } = useChildDocuments();

  const renderChildSiblings = () => {
    return child.siblings && child.siblings.length > 0 ? (
      <>
        <Row className={styles.rowHeadingSpace}>
          <Col md={12}>
            <Typography variant="smallHeading">
              {t("childDataStep.siblings")}
            </Typography>
          </Col>
        </Row>
        {child.siblings.map((sibling) => {
          return (
            <React.Fragment key={sibling.id}>
              <Row className={styles.rowLargeSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.firstName")}
                    value={sibling.firstName}
                    helperText={t("common.birthRegistry")}
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.lastName")}
                    value={sibling.lastName}
                    helperText={t("common.birthRegistry")}
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.oib")}
                    value={sibling.oib}
                    helperText={t("common.oibApi")}
                    plaintext
                  />
                </Col>
              </Row>
              <Row className={styles.rowLargeSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.dateOfBirth")}
                    value={hrDate(sibling.dateOfBirth)}
                    helperText={t("common.birthRegistry")}
                    plaintext
                  />
                </Col>
              </Row>
            </React.Fragment>
          );
        })}
      </>
    ) : null;
  };

  return (
    <>
      <Row className={styles.rowSpace}>
        <Col md={12}>
          <ExpandablePanel clickableHeader>
            <ExpandablePanelHeader onClick={onExpandablePanelClick}>
              {t("requestReviewStep.childInformation")}
            </ExpandablePanelHeader>
            <ExpandablePanelBody>
              <Row className={styles.rowHeadingSpace}>
                <Col md={10}>
                  <Typography variant="smallHeading">
                    {t("person.childPersonalInformation")}
                  </Typography>
                </Col>
                {moveToSpecificStep && (
                  <Col md={2}>
                    <Button
                      size="sm"
                      outline
                      onClick={() => moveToSpecificStep(1)}
                    >
                      {t("common.edit")}
                    </Button>
                  </Col>
                )}
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.firstName")}
                    helperText={t("common.birthRegistry")}
                    value={child?.firstName || emptyValue}
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.lastName")}
                    helperText={t("common.birthRegistry")}
                    value={child?.lastName || emptyValue}
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.oib")}
                    helperText={t("common.oibApi")}
                    value={child?.oib || emptyValue}
                    plaintext
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.dateOfBirth")}
                    helperText={t("common.birthRegistry")}
                    value={
                      child && child.dateOfBirth
                        ? hrDate(child?.dateOfBirth)
                        : emptyValue
                    }
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.sex")}
                    helperText={t("common.birthRegistry")}
                    value={child?.sexName || emptyValue}
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.birthplace")}
                    helperText={t("common.birthRegistry")}
                    value={child?.birthplaceName || emptyValue}
                    plaintext
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.countryBirth")}
                    helperText={t("common.birthRegistry")}
                    value={child?.countryOfBirthName || emptyValue}
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.citizenship")}
                    helperText={t("common.birthRegistry")}
                    value={child?.citizenshipName || emptyValue}
                    plaintext
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.nationalMinority")}
                    value={child.nationalMinorityName || emptyValue}
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.nationality")}
                    value={child.nationalityName || emptyValue}
                    plaintext
                  />
                </Col>
              </Row>
              {renderChildSiblings()}
              <Row className={styles.rowHeadingSpace}>
                <Col md={12}>
                  <Typography variant="smallHeading">
                    {t("person.permanentResidenceInformation")}
                  </Typography>
                </Col>
              </Row>
              <Row className={styles.rowHeadingSpace}>
                <Col md={12}>
                  {child?.integrationResultStatus ? (
                    child.integrationResultStatus ===
                    IntegrationResultStatusEnum.OibFailed ? (
                      <InfoPanel
                        message={t("person.childPermanentResidenceError")}
                        color="danger"
                        className={styles.errorMessage}
                      />
                    ) : (
                      <InfoPanel
                        message={t("common.mupResidenceInfoMessage")}
                        color="success"
                      />
                    )
                  ) : null}
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.country"
                    )}`}
                    value={child?.permanentResidence?.countryName || emptyValue}
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.municipality"
                    )}`}
                    value={
                      child?.permanentResidence?.municipalityName || emptyValue
                    }
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.town"
                    )}`}
                    value={child?.permanentResidence?.townName || emptyValue}
                    plaintext
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.postalCode"
                    )}`}
                    value={
                      child?.permanentResidence?.townPostalCode || emptyValue
                    }
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.street"
                    )}`}
                    value={child?.permanentResidence?.street || emptyValue}
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.streetNumber"
                    )}`}
                    value={
                      child?.permanentResidence?.streetNumber || emptyValue
                    }
                    plaintext
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.streetNumberAddition"
                    )}`}
                    value={
                      child?.permanentResidence?.streetNumberAddition ||
                      emptyValue
                    }
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.dateFrom"
                    )}`}
                    value={
                      child?.permanentResidence?.dateFrom
                        ? hrDate(child.permanentResidence.dateFrom)
                        : emptyValue
                    }
                    plaintext
                  />
                </Col>
              </Row>
              {child?.temporaryResidence && (
                <>
                  <Row className={styles.rowHeadingSpace}>
                    <Col md={12}>
                      <Typography variant="smallHeading">
                        {t("person.temporaryResidenceInformation")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row className={styles.rowHeadingSpace}>
                    <Col md={12}>
                      {child?.integrationResultStatus ? (
                        child.integrationResultStatus ===
                        IntegrationResultStatusEnum.MupFailed ? (
                          <InfoPanel
                            message={t("person.childTemporaryResidenceError")}
                            color="danger"
                            className={styles.errorMessage}
                          />
                        ) : (
                          <InfoPanel
                            message={t("common.mupResidenceInfoMessage")}
                            color="success"
                          />
                        )
                      ) : null}
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.country"
                        )}`}
                        value={
                          child?.temporaryResidence?.countryName || emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.municipality"
                        )}`}
                        value={
                          child?.temporaryResidence?.municipalityName ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.town"
                        )}`}
                        value={
                          child?.temporaryResidence?.townName || emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.postalCode"
                        )}`}
                        value={
                          child?.temporaryResidence?.townPostalCode ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.street"
                        )}`}
                        value={child?.temporaryResidence?.street || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.streetNumber"
                        )}`}
                        value={
                          child?.temporaryResidence?.streetNumber || emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.streetNumberAddition"
                        )}`}
                        value={
                          child?.temporaryResidence?.streetNumberAddition ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.dateFrom"
                        )}`}
                        value={
                          child?.temporaryResidence?.dateFrom
                            ? hrDate(child.temporaryResidence.dateFrom)
                            : emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                </>
              )}

              <Row className={styles.rowSpace}>
                <Col md={12}>
                  <Typography variant="smallHeading">
                    {t("documentUpload.additionalDocumentationList")}
                  </Typography>
                  <DocumentList
                    documents={child?.documents || []}
                    onDocumentClick={handleDownloadDocument}
                    removable={false}
                    fullWidth
                  />
                </Col>
              </Row>
            </ExpandablePanelBody>
          </ExpandablePanel>
        </Col>
      </Row>
    </>
  );
}
